/**
 * Servicio de Recepción de Tokens
 * Maneja la funcionalidad para recibir tokens (QR, dirección, etc.)
 */

import { generateQRCode, generateQRDataURL, QRCodeOptions } from '@/utils/qr.utils';
import { copyToClipboard } from '@/utils/clipboard';
import WalletManager from './wallet-manager';
import storageService from './storage.service';

export interface ReceiveAddressInfo {
  address: string;
  network: string;
  networkName: string;
  isValid: boolean;
}

export interface ReceiveQRResult {
  success: boolean;
  address?: string;
  network?: string;
  qrDataURL?: string;
  receiveURI?: string;
  error?: string;
}

/**
 * Servicio de Recepción
 */
export const receiveService = {
  /**
   * Obtiene la dirección actual de la wallet para recibir tokens
   */
  getCurrentReceiveAddress(): ReceiveAddressInfo | null {
    try {
      // Intentar obtener desde wallet manager (si está desbloqueada)
      const walletManager = WalletManager.getInstance();
      if (walletManager.isUnlocked()) {
        const walletInfo = walletManager.getWalletInfo();
        if (walletInfo) {
          const selectedNetwork = storageService.getSelectedNetwork();
          return {
            address: walletInfo.address,
            network: selectedNetwork,
            networkName: this.getNetworkDisplayName(selectedNetwork),
            isValid: true,
          };
        }
      }

      // Fallback: obtener desde localStorage
      const walletData = storageService.loadWalletData();
      if (walletData && walletData.address) {
        const selectedNetwork = walletData.selectedNetwork || 'omdb';
        return {
          address: walletData.address,
          network: selectedNetwork,
          networkName: this.getNetworkDisplayName(selectedNetwork),
          isValid: true,
        };
      }

      console.warn('[Receive Service] No se encontró dirección de wallet');
      return null;
    } catch (error) {
      console.error('[Receive Service] Error obteniendo dirección:', error);
      return null;
    }
  },

  /**
   * Genera un código QR para la dirección actual
   */
  async generateReceiveQR(
    canvas: HTMLCanvasElement,
    options?: QRCodeOptions
  ): Promise<ReceiveQRResult> {
    try {
      const addressInfo = this.getCurrentReceiveAddress();
      if (!addressInfo) {
        return {
          success: false,
          error: 'No se encontró dirección de wallet activa',
        };
      }

      await generateQRCode(addressInfo.address, canvas, options);

      console.log('[Receive Service] QR generado para:', addressInfo.address);

      return {
        success: true,
        address: addressInfo.address,
        network: addressInfo.network,
      };
    } catch (error) {
      console.error('[Receive Service] Error generando QR:', error);
      return {
        success: false,
        error: (error as Error).message,
      };
    }
  },

  /**
   * Genera un código QR como Data URL
   */
  async generateReceiveQRDataURL(options?: QRCodeOptions): Promise<ReceiveQRResult> {
    try {
      const addressInfo = this.getCurrentReceiveAddress();
      if (!addressInfo) {
        return {
          success: false,
          error: 'No se encontró dirección de wallet activa',
        };
      }

      const qrResult = await generateQRDataURL(addressInfo.address, options);
      if (!qrResult.success) {
        return {
          success: false,
          error: qrResult.error || 'Error generando QR',
        };
      }

      return {
        success: true,
        address: addressInfo.address,
        network: addressInfo.network,
        qrDataURL: qrResult.dataURL,
      };
    } catch (error) {
      console.error('[Receive Service] Error generando QR data URL:', error);
      return {
        success: false,
        error: (error as Error).message,
      };
    }
  },

  /**
   * Genera un código QR para la dirección actual con un monto opcional
   */
  async generateReceiveQRWithAmount(
    canvas: HTMLCanvasElement,
    amount?: string,
    tokenSymbol?: string,
    tokenAddress?: string,
    options?: QRCodeOptions
  ): Promise<ReceiveQRResult> {
    try {
      const addressInfo = this.getCurrentReceiveAddress();
      if (!addressInfo) {
        return {
          success: false,
          error: 'No se encontró dirección de wallet activa',
        };
      }

      const receiveURI = this.buildReceiveURI(
        addressInfo.address,
        addressInfo.network,
        amount,
        tokenSymbol,
        tokenAddress
      );

      await generateQRCode(receiveURI, canvas, options);

      console.log('[Receive Service] QR generado con URI:', receiveURI);

      return {
        success: true,
        address: addressInfo.address,
        network: addressInfo.network,
        receiveURI
      };
    } catch (error) {
      console.error('[Receive Service] Error generando QR con monto:', error);
      return {
        success: false,
        error: (error as Error).message,
      };
    }
  },

  /**
   * Genera un código QR como Data URL con un monto opcional
   */
  async generateReceiveQRDataURLWithAmount(
    amount?: string,
    tokenSymbol?: string,
    tokenAddress?: string,
    options?: QRCodeOptions
  ): Promise<ReceiveQRResult> {
    try {
      const addressInfo = this.getCurrentReceiveAddress();
      if (!addressInfo) {
        return {
          success: false,
          error: 'No se encontró dirección de wallet activa',
        };
      }

      const receiveURI = this.buildReceiveURI(
        addressInfo.address,
        addressInfo.network,
        amount,
        tokenSymbol,
        tokenAddress
      );

      const qrResult = await generateQRDataURL(receiveURI, options);
      if (!qrResult.success) {
        return {
          success: false,
          error: qrResult.error || 'Error generando QR',
        };
      }

      return {
        success: true,
        address: addressInfo.address,
        network: addressInfo.network,
        qrDataURL: qrResult.dataURL,
        receiveURI
      };
    } catch (error) {
      console.error('[Receive Service] Error generando QR data URL con monto:', error);
      return {
        success: false,
        error: (error as Error).message,
      };
    }
  },

  /**
   * Construye un URI de recepción (EIP-681 o formato OMDB)
   */
  buildReceiveURI(
    address: string,
    network: string,
    amount?: string,
    tokenSymbol?: string,
    tokenAddress?: string
  ): string {
    if (!amount || amount === '0') {
      return address;
    }

    // Formato para OMDB
    if (network === 'omdb' || network === 'omdb-local') {
      let uri = `omdb:${address}?amount=${amount}`;
      if (tokenSymbol) uri += `&token=${tokenSymbol}`;
      if (tokenAddress) uri += `&contract=${tokenAddress}`;
      return uri;
    }

    // Formato EIP-681 para redes EVM
    if (tokenAddress && tokenAddress !== '0x0000000000000000000000000000000000000000') {
      return `ethereum:${tokenAddress}/transfer?address=${address}&uint256=${amount}`;
    }

    return `ethereum:${address}?value=${amount}`;
  },

  /**
   * Copia la dirección actual al portapapeles
   */
  async copyAddressToClipboard(): Promise<{ success: boolean; address?: string; error?: string }> {
    try {
      const addressInfo = this.getCurrentReceiveAddress();
      if (!addressInfo) {
        return {
          success: false,
          error: 'No se encontró dirección de wallet activa',
        };
      }

      const success = await copyToClipboard(addressInfo.address);
      if (!success) {
        throw new Error('Copy failed');
      }

      console.log('[Receive Service] Dirección copiada al portapapeles:', addressInfo.address);
      return {
        success: true,
        address: addressInfo.address,
      };
    } catch (error) {
      console.error('[Receive Service] Error copiando dirección:', error);
      return {
        success: false,
        error: 'No se pudo copiar la dirección al portapapeles',
      };
    }
  },

  /**
   * Valida si una dirección es válida para la red actual
   */
  validateReceiveAddress(address: string, network?: string): boolean {
    try {
      if (!address || typeof address !== 'string') {
        return false;
      }

      // Validación básica de formato Ethereum
      const ethereumAddressRegex = /^0x[a-fA-F0-9]{40}$/;
      if (!ethereumAddressRegex.test(address)) {
        return false;
      }

      // Validaciones adicionales por red si es necesario
      const targetNetwork = network || storageService.getSelectedNetwork();
      
      switch (targetNetwork) {
        case 'ethereum':
        case 'bsc':
        case 'omdb':
          // Todas usan formato Ethereum
          return true;
        default:
          return true;
      }
    } catch (error) {
      console.error('[Receive Service] Error validando dirección:', error);
      return false;
    }
  },

  /**
   * Obtiene información completa para recibir tokens
   */
  getReceiveInfo(): {
    address: string | null;
    network: string;
    networkName: string;
    qrSupported: boolean;
    clipboardSupported: boolean;
  } {
    const addressInfo = this.getCurrentReceiveAddress();
    const selectedNetwork = storageService.getSelectedNetwork();

    return {
      address: addressInfo?.address || null,
      network: selectedNetwork,
      networkName: this.getNetworkDisplayName(selectedNetwork),
      qrSupported: this.isQRSupported(),
      clipboardSupported: this.isClipboardSupported(),
    };
  },

  /**
   * Verifica si el navegador soporta generación de QR
   */
  isQRSupported(): boolean {
    try {
      // Verificar si podemos crear canvas
      const canvas = document.createElement('canvas');
      return !!(canvas.getContext && canvas.getContext('2d'));
    } catch (error) {
      return false;
    }
  },

  /**
   * Verifica si el navegador soporta clipboard API
   */
  isClipboardSupported(): boolean {
    return !!(
      (navigator.clipboard && navigator.clipboard.writeText) ||
      document.execCommand
    );
  },

  /**
   * Obtiene el nombre de display de una red
   */
  getNetworkDisplayName(networkId: string): string {
    const networkNames: Record<string, string> = {
      ethereum: 'Ethereum',
      bsc: 'Binance Smart Chain',
      omdb: 'OMDB Network',
      polygon: 'Polygon',
      arbitrum: 'Arbitrum',
      optimism: 'Optimism',
    };

    return networkNames[networkId] || networkId.toUpperCase();
  },

  /**
   * Formatea una dirección para mostrar (acortada)
   */
  formatAddressForDisplay(address: string, startChars = 6, endChars = 4): string {
    if (!address || address.length <= startChars + endChars) {
      return address;
    }

    return `${address.substring(0, startChars)}...${address.substring(
      address.length - endChars
    )}`;
  },

  /**
   * Genera un enlace para explorador de blockchain
   */
  getExplorerLink(address: string, network?: string): string | null {
    const targetNetwork = network || storageService.getSelectedNetwork();
    
    const explorers: Record<string, string> = {
      ethereum: 'https://etherscan.io/address/',
      bsc: 'https://bscscan.com/address/',
      omdb: 'https://omdbscan.com/address/',
      polygon: 'https://polygonscan.com/address/',
      arbitrum: 'https://arbiscan.io/address/',
      optimism: 'https://optimistic.etherscan.io/address/',
    };

    const baseUrl = explorers[targetNetwork];
    return baseUrl ? `${baseUrl}${address}` : null;
  },

  /**
   * Escucha cambios en la red y actualiza la información
   */
  onNetworkChange(callback: (addressInfo: ReceiveAddressInfo | null) => void): () => void {
    const handleNetworkChange = () => {
      const addressInfo = this.getCurrentReceiveAddress();
      callback(addressInfo);
    };

    // Escuchar eventos de cambio de red
    document.addEventListener('network:changed', handleNetworkChange);
    document.addEventListener('wallet:unlocked', handleNetworkChange);
    document.addEventListener('wallet:locked', handleNetworkChange);

    // Retornar función de cleanup
    return () => {
      document.removeEventListener('network:changed', handleNetworkChange);
      document.removeEventListener('wallet:unlocked', handleNetworkChange);
      document.removeEventListener('wallet:locked', handleNetworkChange);
    };
  },
};

export default receiveService;